/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action, computed } from '@ember/object';
import { alias, readOnly } from '@ember/object/computed';
import { isEmpty } from '@ember/utils';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import EnvironmentService from 'cio/services/environment';
import { isObjectAttributeIdentifier, ObjectIdentifierKey } from 'cio/utils/identifiers';
import { CREATED_IN_CIO_AT } from 'cio/utils/attributes';
import AnalyticsService from 'cio/services/analytics';
import ObjectModel from 'cio/models/object';
import ObjectTypesService from 'cio/services/object-types';

interface ObjectAttributesEditorArgs {
  onReset: () => unknown;
  onSave: (attributes: Record<string, any>, hiddenAttrs: string[]) => unknown;
  proxy: Record<string, any>;
  isNew?: boolean;
  isSaving?: boolean;
  sentAttributes?: string[];
  object: ObjectModel;
}

const ENABLE_SEARCH_AFTER = 10;

export default class ObjectAttributesEditor extends Component<ObjectAttributesEditorArgs> {
  @service environment!: EnvironmentService;
  @service analytics!: AnalyticsService;
  @service('objectTypes') objectTypesService!: ObjectTypesService;

  // hiddenAttrs is used to remove attributes.
  // They are set to empty strings on serialization.
  // See: serializers/object.js
  @alias('args.proxy.hiddenAttrs') hiddenAttrs!: string[];
  @alias('args.proxy.currentAttrs') currentAttrs!: Record<string, any>[];
  @readOnly('args.proxy.isDirty') isDirty!: boolean;
  @readOnly('args.proxy.isInvalid') isInvalid!: boolean;

  @tracked searchTerm = '';

  rejectAttributes = [ObjectIdentifierKey.CioObjectId, CREATED_IN_CIO_AT];

  get isNew() {
    return this.args.isNew ?? false;
  }

  @computed('currentAttrs.[]')
  get mappableAttrs() {
    return this.currentAttrs.filter((attr) => {
      if (this.isNew) {
        return attr.key !== ObjectIdentifierKey.CioObjectId;
      }
      return true;
    });
  }

  get isSearchEnabled() {
    return !this.isNew && this.mappableAttrs.length >= ENABLE_SEARCH_AFTER;
  }

  get filteredAttrs() {
    if (isEmpty(this.searchTerm)) {
      return this.mappableAttrs;
    }

    return this.mappableAttrs.filter((attr) => attr.key.includes(this.searchTerm));
  }

  get identifierAttributes() {
    return this.filteredAttrs.filter((attr) => isObjectAttributeIdentifier(attr.key));
  }

  get otherAttributes() {
    return this.filteredAttrs.filter((attr) => !isObjectAttributeIdentifier(attr.key));
  }

  @action
  reset() {
    this.args.proxy.reset();
    return this.args.onReset();
  }

  @action
  save() {
    let attributes = this.args.proxy.getModelAttributes();
    return this.args.onSave(attributes.attrs, attributes.hiddenAttrs);
  }

  @action
  removeAttribute(attribute: Record<string, any>) {
    this.args.proxy.remove(attribute);
  }

  @action
  addAttribute(key: string) {
    this.args.proxy.add(key);
  }

  @action
  createAttribute() {
    if (this.args.object?.id != null) {
      this.analytics.track('objects', 'object_new_attribute_create_clicked', { model: this.args.object });
    }
    this.args.proxy.create();
  }

  @action
  hideAttribute(attribute: Record<string, any>) {
    this.args.proxy.hideAttribute(attribute);
  }
}
